import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    Field,
    Input,
    MessageBar,
    MessageBarBody,
    MessageBarTitle,
    Radio,
    RadioGroup,
    Spinner,
    Switch,
    Text,
} from '@fluentui/react-components';

// component functions
import * as ComponentFunctions from './ConceptValue.Form.Functions';

// styles
import { useStyles } from './ConceptValues.Styles';

// components
import ContractConceptSelectorComponent from '../../../../commons/component/Inputs/ContractConceptSelector/ContractConceptSelector.Component';
import { CurrencySelectorComponent } from '../../../../commons/component/Inputs/CurrencySelector/CurrencySelectorComponent';

// models
import { ContractConcept } from '../../../../models/Entities/Contracts/ContractConcept';

const fmtAmount = (amount: number): string =>
    amount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ConceptValueFormComponent = (props: ComponentFunctions.Props) => {

    const { t } = useTranslation();

    const styles = useStyles();

    const isEdit = props.ConceptValue !== undefined;
    const consumptionCurrency = props.Consumption?.currencyCode || 'ARS';
    const consumptionAmount = props.Consumption?.amount ?? 0;

    const [draft, setDraft] = useState<ComponentFunctions.ConceptValueDraft>(
        ComponentFunctions.EmptyDraft(consumptionCurrency)
    );
    const [showValidation, setShowValidation] = useState(false);
    const wasSavingRef = useRef(false);

    // El borrador se rearma cada vez que se abre: reabrir en "agregar" despues
    // de haber editado no puede arrastrar los valores de la fila anterior.
    useEffect(() => {
        if (!props.isOpen) return;

        setDraft(props.ConceptValue
            ? ComponentFunctions.DraftFromConceptValue(props.ConceptValue, consumptionCurrency)
            : ComponentFunctions.EmptyDraft(consumptionCurrency));
        setShowValidation(false);
    }, [props.isOpen, props.ConceptValue, consumptionCurrency]);

    // Se cierra solo cuando el guardado termino bien; si fallo, el dialogo queda
    // abierto con el error a la vista y lo tipeado intacto.
    useEffect(() => {
        if (wasSavingRef.current && !props.isSaving && props.saved) {
            props.onClose();
        }
        wasSavingRef.current = props.isSaving;
    }, [props.isSaving, props.saved]);

    const validationErrors = useMemo(() => ComponentFunctions.ValidateDraft(draft), [draft]);

    const amounts = useMemo(
        () => ComponentFunctions.ComputeConceptValueAmounts(draft, consumptionAmount),
        [draft, consumptionAmount]
    );

    // Estable entre renders: el selector reacciona a esta prop, y un array nuevo
    // en cada tecla lo haria recalcular su seleccion mientras se tipea el importe.
    const selectedConceptIds = useMemo(
        () => (draft.contractConceptId ? [draft.contractConceptId] : []),
        [draft.contractConceptId]
    );

    const updateDraft = (patch: Partial<ComponentFunctions.ConceptValueDraft>) =>
        setDraft((current) => ({ ...current, ...patch }));

    const HandleSave = () => {
        if (validationErrors.length > 0) {
            setShowValidation(true);
            return;
        }

        const command = ComponentFunctions.BuildCommand(draft, props.ProtocolConsumptionId, consumptionAmount);

        if (isEdit && props.ConceptValue) {
            props.UpdateConceptValue(props.ProtocolId, { ...command, id: props.ConceptValue.id });
        } else {
            props.CreateConceptValue(props.ProtocolId, command);
        }
    };

    return (
        <Dialog
            open={props.isOpen}
            onOpenChange={(_, data) => { if (!data.open && !props.isSaving) props.onClose(); }}
        >
            <DialogSurface>
                <DialogBody>
                    <DialogTitle>
                        {isEdit ? t('conceptValueForm.titleEdit') : t('conceptValueForm.titleAdd')}
                    </DialogTitle>
                    <DialogContent>
                        <div className={styles.form}>

                            {props.failOnSaving && (
                                <MessageBar intent="error" role="alert">
                                    <MessageBarBody>
                                        <MessageBarTitle>{t('conceptValueForm.errorTitle')}</MessageBarTitle>
                                        {props.errorOnSaving?.ErrorMessage || props.errorOnSaving?.Errors?.join(', ')}
                                    </MessageBarBody>
                                </MessageBar>
                            )}

                            {showValidation && validationErrors.length > 0 && (
                                <MessageBar intent="warning" role="alert">
                                    <MessageBarBody>
                                        {validationErrors.map((key) => t(key)).join(' ')}
                                    </MessageBarBody>
                                </MessageBar>
                            )}

                            {/* El selector trae su propio Field con etiqueta, asi que
                                no se lo envuelve en otro: dos labels anidadas para un
                                mismo control confunden a los lectores de pantalla. */}
                            {props.ContractId ? (
                                <ContractConceptSelectorComponent
                                    ContractId={props.ContractId}
                                    referralDate={props.Consumption?.serviceDueDate}
                                    onSelectedContractConcept={(concepts: ContractConcept[]) => {
                                        const selected = concepts?.[0];
                                        updateDraft({
                                            contractConceptId: selected?.id,
                                            contractConceptName: selected?.concept?.conceptName?.trim() ?? ''
                                        });
                                    }}
                                    ContractConceptsSelected={selectedConceptIds}
                                    MultiselectEnable={false}
                                    noteText={undefined}
                                />
                            ) : (
                                <MessageBar intent="warning">
                                    <MessageBarBody>
                                        {t('conceptValueForm.noContract')}
                                    </MessageBarBody>
                                </MessageBar>
                            )}

                            <Field label={t('conceptValueForm.calcMode')}>
                                <RadioGroup
                                    layout="horizontal"
                                    value={draft.calculationMode}
                                    onChange={(_, data) =>
                                        updateDraft({ calculationMode: data.value as 'percent' | 'amount' })
                                    }
                                >
                                    <Radio value="amount" label={t('conceptValueForm.fixedAmount')} />
                                    <Radio value="percent" label={t('conceptValueForm.percentOfConsumption')} />
                                </RadioGroup>
                            </Field>

                            {draft.calculationMode === 'percent' ? (
                                <Field
                                    label={t('conceptValueForm.percentLabel')}
                                    hint={t('conceptValueForm.percentHint', { amount: fmtAmount(consumptionAmount), currency: consumptionCurrency })}
                                    required
                                >
                                    <Input
                                        type="number"
                                        step={0.01}
                                        contentAfter={<Text>%</Text>}
                                        value={draft.percentValue}
                                        onChange={(e) => updateDraft({ percentValue: e.target.value })}
                                    />
                                </Field>
                            ) : (
                                <Field label={t('conceptValueForm.amount')} required>
                                    <Input
                                        type="number"
                                        step={0.01}
                                        value={draft.value}
                                        onChange={(e) => updateDraft({ value: e.target.value })}
                                    />
                                </Field>
                            )}

                            <Field label={t('conceptValueForm.currency')} required>
                                <CurrencySelectorComponent
                                    errorMessage={undefined}
                                    defaultSelected={draft.currencyCode}
                                    label={undefined}
                                    onCurrencyChange={(currency: string) => updateDraft({ currencyCode: currency })}
                                />
                            </Field>

                            <div className={styles.switchRow}>
                                <Text>{t('conceptValueForm.divisionFee')}</Text>
                                <Switch
                                    checked={draft.haveDivisionFee}
                                    onChange={(_, data) => updateDraft({
                                        haveDivisionFee: data.checked,
                                        // Sin division, todo el importe queda del lado del centro.
                                        feeDivisionPercentage: data.checked ? draft.feeDivisionPercentage : '100',
                                        teamDivisionPercentage: data.checked ? draft.teamDivisionPercentage : '0'
                                    })}
                                />
                            </div>

                            {draft.haveDivisionFee && (
                                <div className={styles.fieldPair}>
                                    <Field label={t('conceptValueForm.center')}>
                                        <Input
                                            type="number"
                                            step={0.01}
                                            contentAfter={<Text>%</Text>}
                                            value={draft.feeDivisionPercentage}
                                            onChange={(e) => updateDraft({ feeDivisionPercentage: e.target.value })}
                                        />
                                    </Field>
                                    <Field label={t('conceptValueForm.medicalTeam')}>
                                        <Input
                                            type="number"
                                            step={0.01}
                                            contentAfter={<Text>%</Text>}
                                            value={draft.teamDivisionPercentage}
                                            onChange={(e) => updateDraft({ teamDivisionPercentage: e.target.value })}
                                        />
                                    </Field>
                                </div>
                            )}

                            <div className={styles.switchRow}>
                                <Text>{t('conceptValueForm.applyWithholding')}</Text>
                                <Switch
                                    checked={draft.applyWithholding}
                                    onChange={(_, data) => updateDraft({ applyWithholding: data.checked })}
                                />
                            </div>

                            {draft.applyWithholding && (
                                <Field label={t('conceptValueForm.withholdingPercent')} required>
                                    <Input
                                        type="number"
                                        step={0.01}
                                        contentAfter={<Text>%</Text>}
                                        value={draft.withholdingPercentage}
                                        onChange={(e) => updateDraft({ withholdingPercentage: e.target.value })}
                                    />
                                </Field>
                            )}

                            {/* Reparto ya resuelto: es lo mismo que despues muestra la
                                grilla de valores, calculado con lo que hay en pantalla. */}
                            <div className={styles.preview}>
                                <div className={styles.previewRow}>
                                    <span className={styles.previewLabel}>{t('conceptValueForm.grossValue')}</span>
                                    <span className={styles.previewValue}>{fmtAmount(amounts.value)}</span>
                                </div>
                                {draft.applyWithholding && (
                                    <div className={styles.previewRow}>
                                        <span className={styles.previewLabel}>{t('conceptValueForm.withholding')}</span>
                                        <span className={styles.previewValueNegative}>−{fmtAmount(amounts.withholdingAmount)}</span>
                                    </div>
                                )}
                                <div className={styles.previewRow}>
                                    <span className={styles.previewLabel}>{t('conceptValueForm.netCenter')}</span>
                                    <span className={styles.previewValue}>
                                        {fmtAmount(draft.applyWithholding ? amounts.finalOwnerAmount : amounts.ownerAmount)}
                                    </span>
                                </div>
                                <div className={styles.previewRow}>
                                    <span className={styles.previewLabel}>{t('conceptValueForm.netTeam')}</span>
                                    <span className={styles.previewValue}>
                                        {fmtAmount(draft.applyWithholding ? amounts.finalTeamAmount : amounts.teamAmount)}
                                    </span>
                                </div>
                                <div className={styles.previewTotalRow}>
                                    <span>{t('conceptValueForm.total')}</span>
                                    <span className={styles.previewValue}>
                                        {fmtAmount(
                                            draft.applyWithholding
                                                ? amounts.finalOwnerAmount + amounts.finalTeamAmount
                                                : amounts.ownerAmount + amounts.teamAmount
                                        )} {draft.currencyCode}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </DialogContent>
                    <DialogActions>
                        <Button appearance="subtle" disabled={props.isSaving} onClick={props.onClose}>
                            {t('common.cancel')}
                        </Button>
                        <Button
                            appearance="primary"
                            disabled={props.isSaving || validationErrors.length > 0}
                            onClick={HandleSave}
                            icon={props.isSaving ? <Spinner size="extra-tiny" /> : undefined}
                        >
                            {props.isSaving ? t('conceptValueForm.saving') : (isEdit ? t('conceptValueForm.saveChanges') : t('conceptValueForm.add'))}
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(ConceptValueFormComponent as any);
